import { useMemo } from "react";
import { Zap, Activity, Leaf, BarChart3, Info } from "lucide-react";
import { useT } from "@/contexts/LanguageContext";

const EnergyCO2Summary = ({ questions, answers, sections }) => {
  const t = useT();
  const sectionNameMap = useMemo(() => {
    const map = {};
    (sections || []).forEach(s => { map[s.id] = s.name; });
    return map;
  }, [sections]);

  const stats = useMemo(() => {
    let totalEnergy = 0;
    let totalCO2 = 0;
    let totalPower = 0;
    const sectionBreakdown = {};

    questions.forEach((q) => {
      const val = answers[q.question_id];
      if (!val || q.response_type !== "numeric") return;
      const numVal = parseFloat(val);
      if (isNaN(numVal)) return;

      const unit = (q.unit || "").toLowerCase();
      const ef = q.emission_factor || 0;
      const section = q.section || "Other";

      if (!sectionBreakdown[section]) {
        sectionBreakdown[section] = { energy: 0, co2: 0, power: 0 };
      }

      if (unit.includes("kwh")) {
        totalEnergy += numVal;
        sectionBreakdown[section].energy += numVal;
        if (ef > 0) {
          const co2 = numVal * ef;
          totalCO2 += co2;
          sectionBreakdown[section].co2 += co2;
        }
      } else if (unit === "kw") {
        totalPower += numVal;
        sectionBreakdown[section].power += numVal;
      } else if (ef > 0) {
        const co2 = numVal * ef;
        totalCO2 += co2;
        sectionBreakdown[section].co2 += co2;
      }
    });

    return { totalEnergy, totalCO2, totalPower, sectionBreakdown };
  }, [questions, answers]);

  const hasData = stats.totalEnergy > 0 || stats.totalCO2 > 0 || stats.totalPower > 0;

  return (
    <div className="energy-co2-summary mt-lg" data-testid="energy-co2-summary">
      <h3 className="summary-title">
        <Zap size={20} /> {t("energy_co2_title")}
      </h3>
      {!hasData ? (
        <div className="summary-empty">
          <Info size={16} />
          <span>{t("energy_co2_empty")}</span>
        </div>
      ) : (
        <>
          <div className="summary-stats-row">
            <div className="summary-stat">
              <div className="summary-stat-icon energy"><Zap size={22} /></div>
              <div className="summary-stat-value">{stats.totalEnergy.toLocaleString(undefined, { maximumFractionDigits: 1 })}</div>
              <div className="summary-stat-label">{t("total_energy_kwh")}</div>
            </div>
            <div className="summary-stat">
              <div className="summary-stat-icon power"><Activity size={22} /></div>
              <div className="summary-stat-value">{stats.totalPower.toLocaleString(undefined, { maximumFractionDigits: 1 })}</div>
              <div className="summary-stat-label">{t("total_power_kw")}</div>
            </div>
            <div className="summary-stat">
              <div className="summary-stat-icon co2"><Leaf size={22} /></div>
              <div className="summary-stat-value">{stats.totalCO2.toLocaleString(undefined, { maximumFractionDigits: 1 })}</div>
              <div className="summary-stat-label">{t("est_co2_kg")}</div>
            </div>
            {stats.totalEnergy > 0 && (
              <div className="summary-stat">
                <div className="summary-stat-icon efficiency"><BarChart3 size={22} /></div>
                <div className="summary-stat-value">
                  {stats.totalPower > 0 ? (stats.totalEnergy / stats.totalPower).toFixed(1) : "-"}
                </div>
                <div className="summary-stat-label">{t("hours_full_load")}</div>
              </div>
            )}
          </div>
          {Object.keys(stats.sectionBreakdown).length > 1 && (
            <div className="summary-breakdown">
              <h4 className="summary-breakdown-title">{t("breakdown_by_section")}</h4>
              <div className="summary-breakdown-table">
                <div className="summary-breakdown-row header">
                  <span>{t("section")}</span><span>{t("energy_kwh")}</span><span>{t("power_kw")}</span><span>{t("co2_kg")}</span>
                </div>
                {Object.entries(stats.sectionBreakdown).map(([section, vals]) => (
                  <div className="summary-breakdown-row" key={section}>
                    <span>{sectionNameMap[section] || section.replace(/_/g, " ")}</span>
                    <span>{vals.energy.toLocaleString(undefined, { maximumFractionDigits: 1 })}</span>
                    <span>{vals.power.toLocaleString(undefined, { maximumFractionDigits: 1 })}</span>
                    <span>{vals.co2.toLocaleString(undefined, { maximumFractionDigits: 1 })}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default EnergyCO2Summary;
